import React from 'react';

import { Box, TablePagination, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';

import type { SxProp } from 'src/types/mui';

import { TableBottomActions } from '../TableBottomActions';

export interface TableFooterPaginationProps {
  count: number;
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions: number[];
  hasNextPage?: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

interface TableFooterProps {
  tableBottomActionsProps: React.ComponentProps<typeof TableBottomActions>;
  paginationProps?: TableFooterPaginationProps;
  selectedCount?: number;
  totalRowCount?: number;
}

export const TableFooter: React.FC<TableFooterProps> = ({
  tableBottomActionsProps,
  paginationProps,
  selectedCount = 0,
  totalRowCount,
}) => {
  const rawPage = paginationProps?.page ?? 0;
  const count = paginationProps?.count ?? -1;
  const rowsPerPage = paginationProps?.rowsPerPage ?? 1;

  // rowCount can shrink under the current page (deletes, refetch with filters) — clamp to
  // the last valid page instead of rendering an out-of-range "81–85 of 5"
  const lastPageIndex = count >= 0 ? Math.max(0, Math.ceil(count / rowsPerPage) - 1) : null;
  const page = lastPageIndex === null ? rawPage : Math.min(rawPage, lastPageIndex);

  const onPageChange = paginationProps?.onPageChange;
  const onRowsPerPageChange = paginationProps?.onRowsPerPageChange;
  React.useEffect(() => {
    if (page !== rawPage) {
      onPageChange?.(page);
    }
  }, [page, rawPage, onPageChange]);

  const handlePageChange = React.useCallback(
    (_event: unknown, nextPage: number) => onPageChange?.(nextPage),
    [onPageChange],
  );
  const handleRowsPerPageChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onRowsPerPageChange?.(Number(event.target.value)),
    [onRowsPerPageChange],
  );

  const showSelectedCount = selectedCount > 0;
  const showTotalRows = !paginationProps && isNotNil(totalRowCount);
  const showFooterBar = isNotNil(paginationProps) || showSelectedCount || showTotalRows;

  return (
    <React.Fragment>
      {showFooterBar ? (
        <Box sx={footerBarStyles}>
          <Box sx={footerLabelStyles}>
            {showSelectedCount && (
              <Typography variant="body2">
                {selectedCount} {selectedCount === 1 ? 'row' : 'rows'} selected
              </Typography>
            )}
          </Box>
          {paginationProps ? (
            <TablePagination
              component="div"
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={paginationProps.rowsPerPageOptions}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              slotProps={{
                actions: {
                  nextButton: {
                    disabled:
                      count === -1
                        ? paginationProps.hasNextPage === false
                        : (page + 1) * rowsPerPage >= count,
                  },
                },
              }}
            />
          ) : (
            showTotalRows && (
              <Box sx={footerLabelStyles}>
                <Typography variant="body2">Total Rows: {totalRowCount}</Typography>
              </Box>
            )
          )}
        </Box>
      ) : null}
      <TableBottomActions {...tableBottomActionsProps} />
    </React.Fragment>
  );
};

const footerBarStyles: SxProp = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  minHeight: 52,
};

const footerLabelStyles: SxProp = {
  padding: '0 16px',
};
